import { Button } from '@kerfjs/ui/button';
import { Checkbox } from '@kerfjs/ui/checkbox';
import { List } from '@kerfjs/ui/list';
import { ListHeader } from '@kerfjs/ui/list-header';
import { ListInsetControl } from '@kerfjs/ui/list-inset-control';
import { Spacer } from '@kerfjs/ui/spacer';
import { StateBanner } from '@kerfjs/ui/state-banner';
import { DialogSurface } from '@kerfjs/ui/surface-scaffold';
import { Text } from '@kerfjs/ui/text';
import { type SafeHtml } from 'kerfjs';

import { MEMBER_ROLES } from '../../lib/member-roles.js';
import { invitationEmailStatus, memberDisplayName, type MemberRow } from './member-row.js';
import { MemberStatusGroup } from './member-status-group.js';

/**
 * One member's details for an admin (HS2-20JG34): email and status, the invitation
 * email state for a pending member, and their roles. The footer leads with Cancel, then
 * removes the member (cancels the invitation), resends a pending invitation, or saves the
 * roles ("Save", HS2-HKACGM). Rendered
 * open by the Members page while that member's dialog state is set; a failed action
 * shows its message in the dialog.
 */
export function MemberDialog({ member, error }: { member: MemberRow; error: string }): SafeHtml {
  const invited = member.status === 'invited';
  const invitation = invited ? invitationEmailStatus(member) : null;
  return (
    <DialogSurface
      label={memberDisplayName(member)}
      headerActions={<MemberStatusGroup status={member.status} />}
      lightDismiss
      className="member-dialog"
      rootAttributes={{ 'data-action': 'member-dialog', 'data-member-id': member.id, 'data-auto-open-dialog': '' }}
      footer={
        <>
          <Button
            size="default"
            tone="neutral"
            emphasis="plain"
            action="member-dialog-cancel"
            rootAttributes={{ 'data-dialog-close': '' }}
          >
            Cancel
          </Button>
          <Spacer flex />
          <Button
            size="default"
            tone="danger"
            emphasis="plain"
            action="member-remove"
            rootAttributes={{ 'data-member-id': member.id }}
          >
            {invited ? 'Revoke invitation' : 'Remove member'}
          </Button>
          {invited ? (
            <Button
              size="default"
              tone="neutral"
              emphasis="plain"
              action="member-invite-resend"
              rootAttributes={{ 'data-member-id': member.id }}
            >
              Resend Invitation
            </Button>
          ) : null}
          <Button
            size="default"
            tone="brand"
            emphasis="primary"
            action="member-roles-save"
            rootAttributes={{ 'data-member-id': member.id }}
          >
            Save
          </Button>
        </>
      }
    >
      <List gap="m">
        {error === '' ? null : <StateBanner tone="danger" title={error} />}
        {/* The email is labeled like the Roles section, so it reads as a field (HS2-0RP78F). */}
        <div role="group" aria-label="Email">
          <List gap="xs">
            <ListHeader label="Email" size="xs" density="compact" />
            <ListInsetControl sides="rl">
              <List gap="xs">
                <Text>{member.email}</Text>
                {invitation === null ? null : (
                  <Text
                    size="compact"
                    tone={invitation.failed ? 'danger' : 'quiet'}
                    className="member-invitation-status"
                  >
                    {invitation.text}
                  </Text>
                )}
              </List>
            </ListInsetControl>
          </List>
        </div>
        <div role="group" aria-label="Roles">
          <List gap="xs">
            <ListHeader label="Roles" size="xs" density="compact" />
            <ListInsetControl>
              {/* One role per line, a vertical list rather than a wrapping row (HS2-XF4W1Z). */}
              <List gap="xs">
                {MEMBER_ROLES.map((role) => (
                  <Checkbox name="member-role" value={role} checked={member.roles.includes(role)} label={role} />
                ))}
              </List>
            </ListInsetControl>
          </List>
        </div>
      </List>
    </DialogSurface>
  );
}
